import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./SuspectedDiagnosesSection.css";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";
import PulmonologyMotifChips from "../PulmonologyMotifChips/PulmonologyMotifChips";

const diagnosisPresets = ["asthma", "copd", "pneumonia", "embolism", "lungCancer", "tuberculosis", "ild", "pleuralEffusion", "osa", "bronchiectasis", "pulmonaryHypertension"];
const diagnosisCodes = {
  asthma: "J45.909",
  copd: "J44.9",
  pneumonia: "J18.9",
  embolism: "I26.99",
  lungCancer: "C34.90",
  tuberculosis: "A16.9",
  ild: "J84.9",
  pleuralEffusion: "J90",
  osa: "G47.33",
  bronchiectasis: "J47.9",
  pulmonaryHypertension: "I27.20",
};
const normalizeSearch = (value) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function SuspectedDiagnosesSection({ patientId, record, saveRecord, saving, canSave }) {
  const { t } = useTranslation("pulmonology");
  const [diagnoses, setDiagnoses] = useState([]);
  const [query, setQuery] = useState("");
  const persistedRecord = Array.isArray(record) ? record[0] : record;

  useEffect(() => {
    if (!persistedRecord?.diagnoses) return;
    setDiagnoses(persistedRecord.diagnoses.map((code) => {
      const preset = Object.entries(diagnosisCodes).find(([, value]) => value === code)?.[0];
      return {
        code,
        titleKey: preset ? `diagnoses.presets.${preset}` : null,
        descriptionKey: "diagnoses.differentialDiagnosis",
        typeKey: "diagnoses.differential",
      };
    }));
  }, [persistedRecord]);
  const normalizedQuery = normalizeSearch(query.trim());
  const matchesQuery = (value) => normalizeSearch(value).includes(normalizedQuery);
  const filteredDiagnoses = diagnoses.filter((diagnosis) =>
    matchesQuery(`${diagnosis.code} ${diagnosis.titleKey ? t(diagnosis.titleKey) : ""} ${t(diagnosis.descriptionKey)} ${t(diagnosis.typeKey)}`)
  );

  const toggleDiagnosis = (code) => {
    setDiagnoses((items) => items.some((diagnosis) => diagnosis.code === code)
      ? items.filter((diagnosis) => diagnosis.code !== code)
      : (() => {
        const presetKey = Object.keys(diagnosisCodes).find((key) => diagnosisCodes[key] === code);
        return [...items, {
          code,
          titleKey: presetKey ? `diagnoses.presets.${presetKey}` : null,
          descriptionKey: "diagnoses.differentialDiagnosis",
          typeKey: "diagnoses.differential",
        }];
      })());
  };

  const saveDiagnoses = () => saveRecord?.("suspected-diagnoses", {
    diagnoses: diagnoses.map((diagnosis) => diagnosis.code),
  }, persistedRecord?.id);

  return (
    <div className="pulmo-form">
      <input className="pulmo-search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t("diagnoses.searchPlaceholder")} aria-label={t("diagnoses.searchPlaceholder")} />
      <strong className="pulmo-subtitle">{t("diagnoses.presetsTitle")}</strong>
      <PulmonologyMotifChips
        key={`${patientId}-suspected-diagnoses`}
        patientId={patientId}
        group="suspected-diagnoses"
        defaultOptions={diagnosisPresets}
        selected={diagnoses.map((diagnosis) => diagnosis.code)}
        filterText={normalizedQuery}
        matchesFilter={(option) => matchesQuery(`${option} ${diagnosisCodes[option] ? t(`diagnoses.presets.${option}`) : option} ${diagnosisCodes[option] || ""}`)}
        labelForOption={(option) => diagnosisCodes[option] ? t(`diagnoses.presets.${option}`) : option}
        valueForOption={(option) => diagnosisCodes[option] || option}
        onToggle={toggleDiagnosis}
        onRemove={(code) => setDiagnoses((items) => items.filter((diagnosis) => diagnosis.code !== code))}
      />
      <div className="pulmo-diagnoses">{filteredDiagnoses.map((diagnosis) => {
        const title = diagnosis.titleKey ? t(diagnosis.titleKey) : diagnosis.code;
        return <div className="pulmo-diagnosis" key={diagnosis.code}><span className="pulmo-diagnosis__code">{diagnosis.code}</span><div><strong>{title}</strong><small>{t(diagnosis.descriptionKey)}</small></div><span className="pulmo-diagnosis__type">{t(diagnosis.typeKey)}</span><button type="button" aria-label={t("diagnoses.remove", { name: title })} onClick={() => setDiagnoses((items) => items.filter((item) => item.code !== diagnosis.code))}>×</button></div>;
      })}</div>
      <div className="pulmo-actions">
        <PulmonologySaveButton onClick={saveDiagnoses} saving={saving} disabled={!canSave} />
      </div>
      {normalizedQuery && !filteredDiagnoses.length && <p className="pulmo-diagnoses__empty">{t("diagnoses.noResults")}</p>}
    </div>
  );
}
